import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { tokens as c } from '../theme/tokens';

/**
 * Loading indicator with words.
 *
 *   <CircularLoading />                                  // fills the page (or the shell's content area)
 *   <CircularLoading inline label="Loading bookings…" /> // inside a card, tab or table area
 *
 * label: the words under the spinner (default t('loading')); pass '' for none.
 * inline: true keeps it small (min height 160) instead of filling the view.
 * It fades in after 300 ms, so fast loads never flash a spinner.
 * Inside the signed-in shell the full-size version fills the content area
 * only (ResponsiveDrawer sets --dd-loading-min-height), so it never adds a
 * scroll bar; outside it fills the screen (100dvh, so the phone's URL bar
 * does not push it off-centre).
 */
function CircularLoading({ label, inline = false, size = 36, minHeight, sx }) {
  const { t } = useTranslation();
  const text = label === undefined ? t('loading', { defaultValue: 'Loading…' }) : label;
  const heightSx = inline
    ? { minHeight: minHeight !== undefined ? minHeight : 160 }
    : {
        minHeight: 'var(--dd-loading-min-height, 100vh)',
        '@supports (min-height: 100dvh)': { minHeight: 'var(--dd-loading-min-height, 100dvh)' },
      };
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        py: inline ? 3 : 0,
        animation: 'dd-loading-in 200ms ease-out 300ms both',
        '@keyframes dd-loading-in': { from: { opacity: 0 }, to: { opacity: 1 } },
        ...heightSx,
        ...sx,
      }}
    >
      <CircularProgress size={size} thickness={4} sx={{ color: c.ACCENT }} aria-hidden="true" />
      {text ? (
        <Typography variant="body2" sx={{ color: c.INK_2 }}>
          {text}
        </Typography>
      ) : null}
    </Box>
  );
}

export default CircularLoading;
